import { useState, useEffect, useCallback } from "react";
import Dashboard from "./Dashboard";
import Properties from "./Properties";
import Tenants from "./Tenants";
import Payments from "./Payments";
import Maintenance from "./Maintenance";
import Financials from "./Financials";
import Documents from "./Documents";
import LeaseWizard from "./LeaseWizard";
import Communications from "./Communications";
import Settings from "./Settings";
import PropertyDetail from "./PropertyDetail";
import Reports from "./Reports";
import HelpFees from "./HelpFees";
import { OnboardingBanner } from "./Onboarding";

// ── Sub-tab config per page ───────────────────────────────────────────────────
const SUB_TABS = {
  Dashboard:      ["Overview", "Rent Roll", "Maintenance", "Financials", "Invoices"],
  Properties:     ["Overview", "Units", "Leases"],
  Tenants:        ["All Tenants", "Active", "Inactive"],
  Payments:       ["Overview", "Rent Roll", "Invoices", "History"],
  Maintenance:    ["All Tickets", "Open", "In Progress", "Completed"],
  Financials:     ["Overview", "By Month", "By Property", "By Category"],
  Reports:        ["Overview", "Export"],
  Documents:      ["Templates", "Saved"],
  Communications: ["All", "Maintenance Related"],
  Settings:       ["Profile", "Team"],
  Team:           ["Members", "Roles"],
};

// Top-level nav (visible to all roles that have access)
const TOP_NAV = ["Dashboard", "Properties", "Tenants", "Payments", "Maintenance", "Financials", "Reports"];

// ── Inline SVG icons ──────────────────────────────────────────────────────────
function IconSidebar() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/>
    </svg>
  );
}
function IconBell() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
  );
}
function IconGear() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  );
}

// ── Mobile nav icons ──────────────────────────────────────────────────────────
function MobileIcon({ name }) {
  const p = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round" };
  if (name === "Dashboard")    return <svg {...p}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
  if (name === "Tenants")      return <svg {...p}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
  if (name === "Payments")     return <svg {...p}><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
  if (name === "Maintenance")  return <svg {...p}><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>;
  if (name === "More")         return <svg {...p}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
  return null;
}

// ── Global CSS ────────────────────────────────────────────────────────────────
const SHELL_CSS = `
  *, *::before, *::after { box-sizing: border-box; }

  /* Top bar nav buttons */
  .ls-topnav { height: 36px; display: flex; align-items: center; padding: 0 11px;
    border: none; border-bottom: 2px solid transparent; background: transparent;
    color: rgba(245,241,234,0.55); font-family: 'Manrope',sans-serif; font-size: 15px;
    font-weight: 500; cursor: pointer; white-space: nowrap; letter-spacing: 0.03em;
    transition: background 0.12s, color 0.12s, border-color 0.12s; }
  .ls-topnav:hover  { background: rgba(245,241,234,0.05); color: #F5F1EA; }
  .ls-topnav.active { color: #F5F1EA; border-bottom-color: #7FB98E; }

  /* Sidebar toggle */
  .ls-sb-toggle { width: 36px; height: 36px; display: flex; align-items: center;
    justify-content: center; border: none; border-right: 0.5px solid rgba(245,241,234,0.1);
    background: transparent; color: rgba(245,241,234,0.45); cursor: pointer;
    flex-shrink: 0; transition: background 0.12s, color 0.12s; }
  .ls-sb-toggle:hover     { background: rgba(245,241,234,0.07); color: #F5F1EA; }
  .ls-sb-toggle.sb-open   { background: rgba(127,185,142,0.1);  color: #7FB98E; }

  /* Secondary nav tabs */
  .ls-subtab { height: 30px; padding: 0 13px; border: none; border-bottom: 2px solid transparent;
    background: transparent; font-family: 'Manrope',sans-serif; font-size: 10px;
    font-weight: 500; color: #9B9690; cursor: pointer; white-space: nowrap;
    transition: color 0.12s, border-color 0.12s; }
  .ls-subtab:hover  { color: #163C2F; }
  .ls-subtab.active { color: #163C2F; border-bottom-color: #163C2F; font-weight: 600; }

  /* Filter chips */
  .ls-chip { padding: 3px 9px; border-radius: 999px; border: 0.5px solid #E8E4DC;
    font-family: 'Manrope',sans-serif; font-size: 10px; cursor: pointer;
    background: #fff; color: #9B9690; transition: background 0.12s, color 0.12s; flex-shrink: 0; }
  .ls-chip.active { background: #163C2F; color: #F5F1EA; border-color: #163C2F; }

  /* Sidebar items */
  .ls-sbi { display: flex; align-items: center; justify-content: space-between;
    padding: 6px 10px 6px 12px; font-family: 'Manrope',sans-serif; font-size: 13px;
    color: #2B2B2B; cursor: pointer; border-left: 2px solid transparent;
    border-radius: 0 3px 3px 0; white-space: nowrap;
    transition: background 0.12s, color 0.12s; }
  .ls-sbi:hover  { background: #F5F1EA; }
  .ls-sbi.active { background: #EAF2ED; color: #163C2F; border-left-color: #163C2F; font-weight: 600; }

  /* Icon buttons in top bar */
  .ls-icon-btn { width: 28px; height: 28px; display: flex; align-items: center;
    justify-content: center; border: none; background: transparent;
    color: rgba(245,241,234,0.5); cursor: pointer; border-radius: 3px;
    transition: background 0.12s, color 0.12s; }
  .ls-icon-btn:hover { background: rgba(245,241,234,0.08); color: #F5F1EA; }

  /* Mobile nav */
  .ls-mobile-nav { display: none; position: fixed; bottom: 0; left: 0; right: 0;
    height: 58px; background: #163C2F; border-top: 0.5px solid rgba(245,241,234,0.1);
    z-index: 50; flex-direction: row; align-items: stretch; }
  .ls-mob-item { flex: 1; display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: 3px; border: none; background: transparent;
    cursor: pointer; color: rgba(245,241,234,0.4); font-size: 9px; font-weight: 500;
    font-family: 'Manrope',sans-serif; padding: 4px 2px; transition: color 0.15s; }
  .ls-mob-item.active { color: #7FB98E; }

  /* More drawer */
  .ls-more-overlay { position: fixed; inset: 0; z-index: 55; background: rgba(0,0,0,0.45); display: none; }
  .ls-more-overlay.open { display: block; }
  .ls-more-drawer { position: absolute; bottom: 58px; left: 0; right: 0;
    background: #163C2F; border-radius: 14px 14px 0 0; padding: 14px 14px 18px;
    display: grid; grid-template-columns: repeat(3,1fr); gap: 8px;
    box-shadow: 0 -4px 24px rgba(0,0,0,0.25); }
  .ls-more-item { display: flex; flex-direction: column; align-items: center; gap: 5px;
    padding: 12px 6px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.04); cursor: pointer; color: rgba(245,241,234,0.6);
    font-size: 11px; font-weight: 500; font-family: 'Manrope',sans-serif;
    transition: background 0.12s, color 0.12s; }
  .ls-more-item.active { background: #214D3A; color: #F5F1EA; border-color: #7FB98E; }

  @media (max-width: 768px) {
    .ls-mobile-nav { display: flex !important; }
    .ls-topnav-links { display: none !important; }
    .ls-search-wrap { display: none !important; }
    .ls-sidebar { display: none !important; }
    .ls-main { margin-left: 0 !important; padding: 12px 12px 72px !important; }
    .ls-filter-bar { overflow-x: auto; }
    .ls-subnav-wrap { overflow-x: auto; }
    .ls-sec-nav-actions { display: none !important; }
  }
`;

export default function AppShell({ supabase, ...d }) {
  const [sidebarOpen, setSidebarOpen] = useState(
    () => localStorage.getItem("leasly_sb2") === "true"
  );
  // The secondary nav bar now only ever holds page sub-tabs, and only
  // Dashboard/Settings consume those (see SUB_TABS below) — every other
  // page renders no bar at all, not just an empty one, so the layout
  // offsets further down shrink to match rather than leaving a blank strip.
  const showSecondaryNav = d.page === "Dashboard" || d.page === "Settings";
  const contentTopOffset = showSecondaryNav ? 66 : 36; // header (36) + secondary nav (30) when present
  const [subTab, setSubTab] = useState(
    () => (SUB_TABS[d.page] || ["Overview"])[0]
  );
  const [activeProp,   setActiveProp]   = useState("All");
  const [activeStatus, setActiveStatus] = useState("All");
  const [filterSearch, setFilterSearch] = useState("");
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });
  const [dashPeriod, setDashPeriod] = useState("month"); // "month" | "3month" | "annual"
  const [finTab, setFinTab] = useState("month");
  const [finPeriodFilter, setFinPeriodFilter] = useState(null); // null | "Q1"–"Q4" | "YTD"
  const [finExportTrigger, setFinExportTrigger] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const fetchNotifications = useCallback(() => {
    if (!d.session?.user?.id) return;
    supabase.from("notifications").select("*").eq("user_id", d.session.user.id)
      .order("created_at", { ascending: false }).limit(30)
      .then(({ data }) => setNotifications(data || []));
  }, [d.session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length === 0) return;
    await supabase.from("notifications").update({ read: true }).in("id", unreadIds);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // report_ready notifications store just the title in `message` — the
  // actor-aware sentence is built here at display time from actor_id.
  const notifMessage = (n) => {
    if (n.type !== "report_ready") return n.message;
    if (n.actor_id === d.session?.user?.id) return `You pulled ${n.message} — View now?`;
    const actor = (d.teamMembers || []).find(m => m.user_id === n.actor_id);
    return `${actor?.name || "A team member"} pulled ${n.message} — View here`;
  };

  const handleNotifClick = async (n) => {
    if (!n.read) {
      await supabase.from("notifications").update({ read: true }).eq("id", n.id);
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    }
    if (n.link) d.setPage(n.link);
    setNotifOpen(false);
  };

  // Reset sub-tab when page changes
  useEffect(() => {
    setSubTab((SUB_TABS[d.page] || ["Overview"])[0]);
  }, [d.page]);

  const toggleSidebar = () => {
    const next = !sidebarOpen;
    setSidebarOpen(next);
    localStorage.setItem("leasly_sb2", String(next));
  };

  const userInitial = (() => {
    const full = d.session?.user?.user_metadata?.full_name;
    if (full) return full.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    return (d.session?.user?.email || "U")[0].toUpperCase();
  })();

  // Sidebar counts
  const paidCount    = (d.payments    || []).filter(p => p.status === "paid").length;
  const overdueCount = (d.payments    || []).filter(p => p.status === "overdue").length;
  const openCount    = (d.maintenance || []).filter(m => m.status !== "resolved").length;
  const now          = new Date();
  const monthStr     = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const qStr         = `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`;
  const ytdStr       = `YTD ${now.getFullYear()}`;

  // Which top-nav items to show for this role
  const visibleTop = TOP_NAV.filter(n => {
    if (n === "Reports") return d.userRole === "admin" || d.userRole === "manager";
    return (d.visibleNav || []).includes(n);
  });

  // Mobile nav: first 4 visible items + More
  const mobileMain = visibleTop.slice(0, 4);
  const mobileMore = visibleTop.slice(4);

  // Sidebar active item heuristic
  const sidebarActiveMap = {
    Financials: "Portfolio P&L", Reports: "Tax Summary",
    Payments: "All Paid", Maintenance: "Open Tickets",
  };
  const sidebarActive = (finTab === "tax" && d.page === "Financials")
    ? "Tax Summary"
    : sidebarActiveMap[d.page] || "All Properties";

  return (
    <div style={{ minHeight: "100vh", background: "#F5F1EA", fontFamily: "'Manrope', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{SHELL_CSS}</style>

      {/* ══ TOP BAR ══ */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 36, background: "#163C2F", zIndex: 35, display: "flex", alignItems: "stretch" }}>

        {/* Sidebar toggle */}
        <button className={`ls-sb-toggle${sidebarOpen ? " sb-open" : ""}`} onClick={toggleSidebar} title={sidebarOpen ? "Close panel" : "Open panel"}>
          <IconSidebar />
        </button>

        {/* Logo */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "0 12px", borderRight: "0.5px solid rgba(245,241,234,0.15)", cursor: "pointer", flexShrink: 0 }}
          onClick={() => d.setPage("Dashboard")}
        >
          <div style={{ width: 19, height: 19, background: "#214D3A", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 13, fontWeight: 700, color: "#7FB98E", lineHeight: 1 }}>L</span>
          </div>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: "#F5F1EA", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>Leasly</span>
        </div>

        {/* Nav links */}
        <div className="ls-topnav-links" style={{ display: "flex", flex: 1, overflowX: "auto" }}>
          {visibleTop.map(n => (
            <button key={n} className={`ls-topnav${d.page === n ? " active" : ""}`} onClick={() => d.setPage(n)}>
              {n}
            </button>
          ))}
        </div>

        {/* Right: search + icons + user */}
        <div className="ls-search-wrap" style={{ display: "flex", alignItems: "center", gap: 3, padding: "0 10px", flexShrink: 0 }}>
          <input
            placeholder="Search..."
            style={{ width: 140, height: 22, background: "rgba(245,241,234,0.08)", border: "0.5px solid rgba(245,241,234,0.15)", borderRadius: 4, padding: "0 8px", color: "#F5F1EA", fontFamily: "inherit", fontSize: 10, outline: "none" }}
          />
          <div style={{ position: "relative" }}>
            <button className="ls-icon-btn" title="Notifications" onClick={() => { setNotifOpen(v => !v); fetchNotifications(); }} style={{ position: "relative" }}>
              <IconBell />
              {unreadCount > 0 && (
                <span style={{ position: "absolute", top: 2, right: 2, width: 14, height: 14, borderRadius: "50%", background: "#dc2626", color: "#fff", fontSize: 8, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <>
                <div onClick={() => setNotifOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 340, maxHeight: 420, overflowY: "auto", background: "#fff", border: "1px solid #E8E4DC", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", zIndex: 50, fontFamily: "'Manrope', sans-serif" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid #F0EDE6" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#2B2B2B" }}>Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} style={{ background: "none", border: "none", color: "#163C2F", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                        Mark all as read
                      </button>
                    )}
                  </div>
                  {notifications.length === 0
                    ? <div style={{ padding: "32px 16px", textAlign: "center", color: "#9B9690", fontSize: 13 }}>No notifications yet</div>
                    : notifications.map(n => (
                      <div key={n.id} onClick={() => handleNotifClick(n)} style={{
                        padding: "12px 16px", borderBottom: "1px solid #F5F1EA", cursor: "pointer",
                        background: n.read ? "transparent" : "#FAFAF7",
                        transition: "background 0.1s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = "#F5F1EA"}
                        onMouseLeave={e => e.currentTarget.style.background = n.read ? "transparent" : "#FAFAF7"}>
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                            {n.type === "payment" ? "💰" : n.type === "maintenance" ? "🔧" : n.type === "tenant_added" ? "👤" : n.type === "overdue" ? "⚠️" : n.type === "report_ready" ? "📄" : "📌"}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, color: "#2B2B2B", lineHeight: 1.5, fontWeight: n.read ? 400 : 600 }}>{notifMessage(n)}</div>
                            <div style={{ fontSize: 11, color: "#9B9690", marginTop: 3 }}>
                              {new Date(n.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} at {new Date(n.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                            </div>
                          </div>
                          {!n.read && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#163C2F", flexShrink: 0, marginTop: 6 }} />}
                        </div>
                      </div>
                    ))
                  }
                </div>
              </>
            )}
          </div>
          {/* Upgrade pill — only shown on Free plan */}
          {!d.session?.user?.user_metadata?.tier || d.session?.user?.user_metadata?.tier === "free" ? (
            <a href="/pricing" style={{ height: 20, padding: "0 8px", borderRadius: 3, border: "0.5px solid #B08D57", background: "rgba(176,141,87,0.12)", color: "#B08D57", fontFamily: "inherit", fontSize: 9, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", textDecoration: "none", display: "flex", alignItems: "center", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Upgrade ✦
            </a>
          ) : null}
        </div>
      </div>

      {/* ══ SECONDARY NAV ══ */}
      {/* Only ever holds page sub-tabs now (Dashboard/Settings) — the period
          dropdown and quick-action buttons that used to live on the right
          were redundant with the sidebar's own Period/Actions sections
          (which are global, not page-scoped) and were removed/moved there
          instead of duplicated here. */}
      {showSecondaryNav && (
        <div style={{ position: "fixed", top: 36, left: 0, right: 0, height: 30, background: "#fff", zIndex: 34, borderBottom: "0.5px solid #E8E4DC", display: "flex", alignItems: "stretch" }}>
          <div className="ls-subnav-wrap" style={{ display: "flex", alignItems: "stretch", overflowX: "auto" }}>
            {(SUB_TABS[d.page] || []).map(tab => (
              <button key={tab} className={`ls-subtab${subTab === tab ? " active" : ""}`} onClick={() => setSubTab(tab)}>
                {tab}
              </button>
            ))}
          </div>
        </div>
      )}


      {/* ══ SIDEBAR ══ */}
      <div
        className="ls-sidebar"
        style={{ position: "fixed", left: 0, top: contentTopOffset, bottom: 0, width: sidebarOpen ? 164 : 0, transition: "width 0.2s ease", overflow: "hidden", background: "#fff", borderRight: "0.5px solid #E8E4DC", zIndex: 32 }}
      >
        <div style={{ width: 164, height: "100%", overflowY: "auto", paddingTop: 8, paddingBottom: 10, display: "flex", flexDirection: "column" }}>

          {/* QUICK VIEW */}
          <div style={{ padding: "6px 10px 3px 12px", fontSize: 11, textTransform: "uppercase", color: "#B0ACA6", letterSpacing: "0.1em", fontWeight: 700 }}>Quick View</div>
          {[
            { label: "All Properties", fn: () => d.setPage("Properties") },
            { label: "Portfolio P&L",  fn: () => { setFinTab("month"); setFinPeriodFilter(null); d.setPage("Financials"); } },
            { label: "Tax Summary",    fn: () => { setFinTab("tax");   setFinPeriodFilter(null); d.setPage("Financials"); } },
          ].map(item => (
            <div key={item.label} className={`ls-sbi${sidebarActive === item.label ? " active" : ""}`} onClick={item.fn}>
              <span>{item.label}</span>
            </div>
          ))}

          {/* STATUS */}
          <div style={{ padding: "9px 10px 3px 12px", fontSize: 11, textTransform: "uppercase", color: "#B0ACA6", letterSpacing: "0.1em", fontWeight: 700, marginTop: 4, borderTop: "0.5px solid #F0EDE6" }}>Status</div>
          {[
            { label: "All Paid",     badge: paidCount,    badgeColor: "#22c55e", fn: () => { d.setPage("Payments"); setActiveStatus("Paid"); } },
            { label: "Overdue",      badge: overdueCount, badgeColor: "#dc2626", fn: () => { d.setPage("Payments"); setActiveStatus("Overdue"); } },
            { label: "Open Tickets", badge: openCount,    badgeColor: "#B08D57", fn: () => d.setPage("Maintenance") },
          ].map(item => (
            <div key={item.label} className={`ls-sbi${sidebarActive === item.label ? " active" : ""}`} onClick={item.fn}>
              <span>{item.label}</span>
              {item.badge > 0 && (
                <span style={{ fontSize: 10, background: item.badgeColor, color: "#fff", borderRadius: 999, padding: "1px 5px", fontWeight: 700 }}>{item.badge}</span>
              )}
            </div>
          ))}

          {/* PERIOD */}
          <div style={{ padding: "9px 10px 3px 12px", fontSize: 11, textTransform: "uppercase", color: "#B0ACA6", letterSpacing: "0.1em", fontWeight: 700, marginTop: 4, borderTop: "0.5px solid #F0EDE6" }}>Period</div>
          {(() => {
            const currentMonthValue = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
            const currentQ = `Q${Math.ceil((now.getMonth() + 1) / 3)}`;
            return [
              { label: monthStr, active: selectedPeriod === currentMonthValue && !finPeriodFilter, fn: () => { setSelectedPeriod(currentMonthValue); setFinPeriodFilter(null); d.setPage("Financials"); } },
              { label: qStr,     active: finPeriodFilter === currentQ,                             fn: () => { setFinPeriodFilter(currentQ); setSelectedPeriod(null); d.setPage("Financials"); } },
              { label: ytdStr,   active: finPeriodFilter === "YTD",                               fn: () => { setFinPeriodFilter("YTD");    setSelectedPeriod(null); d.setPage("Financials"); } },
            ];
          })().map(item => (
            <div key={item.label} className={`ls-sbi${item.active ? " active" : ""}`} onClick={item.fn}>
              <span>{item.label}</span>
            </div>
          ))}

          {/* ACTIONS */}
          <div style={{ padding: "9px 10px 3px 12px", fontSize: 11, textTransform: "uppercase", color: "#B0ACA6", letterSpacing: "0.1em", fontWeight: 700, marginTop: 4, borderTop: "0.5px solid #F0EDE6" }}>Actions</div>
          {/* Moved here from the old top secondary-nav bar — same onClick
              handlers and permission gates, plain ls-sbi row styling to
              match Export CSV/Add Expense/New Lease below. */}
          {d.can?.addTenant && (
            <div className="ls-sbi" onClick={() => { d.setPage("Tenants"); d.setShowAddTenant(true); }}>
              <span>Add Tenant</span>
            </div>
          )}
          {d.can?.addPayment && (
            <div className="ls-sbi" onClick={() => { d.setPage("Payments"); d.setShowAddPayment(true); }}>
              <span>Record Payment</span>
            </div>
          )}
          {[
            { label: "Export CSV",  fn: () => { setFinExportTrigger(t => t + 1); d.setPage("Financials"); } },
            { label: "Add Expense", fn: () => { d.setShowAddExpense(true); d.setPage("Financials"); } },
            { label: "New Lease",   fn: () => d.setShowLeaseWizard(true) },
          ].map(item => (
            <div key={item.label} className="ls-sbi" onClick={item.fn}><span>{item.label}</span></div>
          ))}

          {/* User profile + actions */}
          <div style={{ marginTop: "auto", borderTop: "0.5px solid #F0EDE6", padding: "12px 10px 4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#EAF2ED", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#163C2F" }}>{userInitial}</span>
              </div>
              <div style={{ overflow: "hidden", minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#2B2B2B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {d.session?.user?.user_metadata?.full_name || d.session?.user?.email?.split("@")[0] || "User"}
                </div>
                <div style={{ fontSize: 10, color: "#9B9690", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {d.session?.user?.email || ""}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => d.setPage("Settings")} title="Settings"
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "5px 0", borderRadius: 4, border: "0.5px solid #E8E4DC", background: "transparent", color: "#9B9690", fontFamily: "inherit", fontSize: 10, cursor: "pointer" }}>
                <IconGear /> Settings
              </button>
              <button onClick={async () => { await supabase.auth.signOut(); window.location.href = "/"; }} title="Sign out"
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "5px 0", borderRadius: 4, border: "0.5px solid #E8E4DC", background: "transparent", color: "#9B9690", fontFamily: "inherit", fontSize: 10, cursor: "pointer" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══ MAIN CONTENT ══ */}
      <div
        className="ls-main"
        style={{ marginTop: contentTopOffset, marginLeft: sidebarOpen ? 164 : 0, padding: "20px 24px", transition: "margin-left 0.2s ease", minHeight: `calc(100vh - ${contentTopOffset}px)` }}
      >
        {d.page === "Dashboard" && !d.companyProfile?.onboarding_complete && (
          <OnboardingBanner
            supabase={supabase}
            session={d.session}
            properties={d.properties}
            tenants={d.tenants}
            companyProfile={d.companyProfile}
            setShowAddProperty={d.setShowAddProperty}
            setShowAddTenant={d.setShowAddTenant}
            setPage={d.setPage}
          />
        )}
        {d.page === "Dashboard" && (
          <Dashboard
            can={d.can}
            setShowAddPayment={d.setShowAddPayment}
            setShowAddTenant={d.setShowAddTenant}
            expiringLeases={d.expiringLeases}
            rentIncreases={d.rentIncreases}
            tenants={d.tenants}
            payments={d.payments}
            maintenance={d.maintenance}
            expenses={d.expenses}
            properties={d.properties}
            totalRent={d.totalRent}
            collected={d.collected}
            overdueAmt={d.overdueAmt}
            openTickets={d.openTickets}
            totalExpenses={d.totalExpenses}
            setPage={d.setPage}
            setPropTab={d.setPropTab}
            setViewMaint={d.setViewMaint}
            setSelectedTenant={d.setSelectedTenant}
            changePaymentStatus={d.changePaymentStatus}
            subTab={subTab}
            setSubTab={setSubTab}
            selectedPeriod={selectedPeriod}
            dashPeriod={dashPeriod}
            setDashPeriod={setDashPeriod}
            activePropFilter={activeProp}
            activeStatusFilter={activeStatus}
            filterSearch={filterSearch}
          />
        )}
        {d.page === "Properties" && (
          <Properties
            session={d.session}
            properties={d.properties} tenants={d.tenants} rentIncreases={d.rentIncreases}
            leases={d.leases} getLeasePdfUrl={d.getLeasePdfUrl}
            propTab={d.propTab} setPropTab={d.setPropTab} can={d.can}
            seedDefaultProperties={d.seedDefaultProperties} setShowAddProperty={d.setShowAddProperty}
            setSelectedTenant={d.setSelectedTenant} setPage={d.setPage}
            editProperty={d.editProperty} setEditProperty={d.setEditProperty}
            savePropertyEdit={d.savePropertyEdit} deleteProperty={d.deleteProperty}
            applyRentIncrease={d.applyRentIncrease} deleteRentIncrease={d.deleteRentIncrease}
            newRentIncrease={d.newRentIncrease} setNewRentIncrease={d.setNewRentIncrease}
            showAddRentIncrease={d.showAddRentIncrease} setShowAddRentIncrease={d.setShowAddRentIncrease}
            addRentIncrease={d.addRentIncrease}
            showAddProperty={d.showAddProperty} newProperty={d.newProperty}
            setNewProperty={d.setNewProperty} addProperty={d.addProperty}
            syncPropertiesFromTenants={d.syncPropertiesFromTenants}
            setSelectedProperty={d.setSelectedProperty}
            setShowLeaseWizard={d.setShowLeaseWizard}
          />
        )}
        {d.page === "PropertyDetail" && d.selectedProperty && (
          <PropertyDetail
            property={d.selectedProperty}
            tenants={d.tenants}
            payments={d.payments}
            maintenance={d.maintenance}
            expenses={d.expenses}
            setPage={d.setPage}
            setSelectedProperty={d.setSelectedProperty}
            supabase={supabase}
            session={d.session}
          />
        )}
        {d.page === "Tenants" && (
          <Tenants
            tenants={d.tenants} payments={d.payments} communications={d.communications}
            tenantPhotos={d.tenantPhotos}
            searchTenant={d.searchTenant} setSearchTenant={d.setSearchTenant}
            selectedTenant={d.selectedTenant} setSelectedTenant={d.setSelectedTenant}
            can={d.can} setPage={d.setPage}
            showAddTenant={d.showAddTenant} setShowAddTenant={d.setShowAddTenant}
            newTenant={d.newTenant} setNewTenant={d.setNewTenant} addTenant={d.addTenant}
            editTenant={d.editTenant} setEditTenant={d.setEditTenant} saveTenantEdit={d.saveTenantEdit}
            deleteTenant={d.deleteTenant}
            showTenantHistory={d.showTenantHistory} setShowTenantHistory={d.setShowTenantHistory}
            photoType={d.photoType} setPhotoType={d.setPhotoType}
            photoLabel={d.photoLabel} setPhotoLabel={d.setPhotoLabel}
            photoUploadFile={d.photoUploadFile} setPhotoUploadFile={d.setPhotoUploadFile}
            photoUploading={d.photoUploading}
            uploadTenantPhoto={d.uploadTenantPhoto} deleteTenantPhoto={d.deleteTenantPhoto}
            setShowCommLog={d.setShowCommLog} setNewComm={d.setNewComm} setShowAddComm={d.setShowAddComm}
          />
        )}
        {d.page === "Payments" && (
          <Payments
            tenants={d.tenants} payments={d.payments} settings={d.settings}
            paymentEdits={d.paymentEdits} session={d.session} can={d.can}
            collected={d.collected} overdueAmt={d.overdueAmt}
            collapsedMonths={d.collapsedMonths} setCollapsedMonths={d.setCollapsedMonths}
            showAddPayment={d.showAddPayment} setShowAddPayment={d.setShowAddPayment}
            newPayment={d.newPayment} setNewPayment={d.setNewPayment} addPayment={d.addPayment}
            newPaymentError={d.newPaymentError} setNewPaymentError={d.setNewPaymentError}
            quickPayError={d.quickPayError} setQuickPayError={d.setQuickPayError}
            invoiceModal={d.invoiceModal} setInvoiceModal={d.setInvoiceModal}
            invoiceView={d.invoiceView} setInvoiceView={d.setInvoiceView}
            invoiceResult={d.invoiceResult} setInvoiceResult={d.setInvoiceResult}
            invoiceSending={d.invoiceSending} sendVia={d.sendVia} setSendVia={d.setSendVia}
            sendInvoice={d.sendInvoice}
            companyProfile={d.companyProfile}
            showEditPayment={d.showEditPayment} setShowEditPayment={d.setShowEditPayment}
            editPaymentDraft={d.editPaymentDraft} setEditPaymentDraft={d.setEditPaymentDraft}
            editPaymentError={d.editPaymentError} setEditPaymentError={d.setEditPaymentError}
            editInternalNotes={d.editInternalNotes} setEditInternalNotes={d.setEditInternalNotes}
            savePaymentEdit={d.savePaymentEdit}
            handleMarkPaid={d.handleMarkPaid} quickCreatePayment={d.quickCreatePayment}
            deletePayment={d.deletePayment} openInvoice={d.openInvoice}
            autoGeneratePayments={d.autoGeneratePayments} runLateFeeCheck={d.runLateFeeCheck}
            paymentAuditLog={d.paymentAuditLog} changePaymentStatus={d.changePaymentStatus}
            selectedPeriod={selectedPeriod}
          />
        )}
        {/* Maintenance — full page when on Maintenance, modals-only otherwise so
            tickets opened from the Dashboard still show the detail view.        */}
        <Maintenance
          showFullPage={d.page === "Maintenance"}
          maintenance={d.maintenance} tenants={d.tenants} openTickets={d.openTickets} can={d.can}
          viewMaint={d.viewMaint} setViewMaint={d.setViewMaint}
          editMaintMode={d.editMaintMode} setEditMaintMode={d.setEditMaintMode}
          editMaintData={d.editMaintData} setEditMaintData={d.setEditMaintData}
          saveMaintenanceEdit={d.saveMaintenanceEdit} updateMaintFull={d.updateMaintFull}
          showAddMaint={d.showAddMaint} setShowAddMaint={d.setShowAddMaint}
          newMaint={d.newMaint} setNewMaint={d.setNewMaint}
          addMaintenance={d.addMaintenance} deleteMaintenance={d.deleteMaintenance}
          showResolveMaint={d.showResolveMaint} setShowResolveMaint={d.setShowResolveMaint}
          resolveDate={d.resolveDate} setResolveDate={d.setResolveDate}
          maintNotes={d.maintNotes} maintNoteInput={d.maintNoteInput}
          setMaintNoteInput={d.setMaintNoteInput} postMaintNote={d.postMaintNote}
          session={d.session} settings={d.settings} companyProfile={d.companyProfile}
        />
        {d.page === "Financials" && (
          <Financials
            payments={d.payments} expenses={d.expenses} tenants={d.tenants}
            maintenance={d.maintenance} properties={d.properties} units={d.units}
            selectedYear={d.selectedYear} setSelectedYear={d.setSelectedYear}
            selectedPeriod={selectedPeriod}
            showAddExpense={d.showAddExpense} setShowAddExpense={d.setShowAddExpense}
            newExpense={d.newExpense} setNewExpense={d.setNewExpense}
            addExpense={d.addExpense} deleteExpense={d.deleteExpense}
            finTab={finTab} setFinTab={setFinTab}
            finPeriodFilter={finPeriodFilter} setFinPeriodFilter={setFinPeriodFilter}
            finExportTrigger={finExportTrigger}
          />
        )}
        {d.page === "Reports" && (
          <Reports
            session={d.session}
            payments={d.payments}
            tenants={d.tenants}
            expenses={d.expenses}
            properties={d.properties}
            units={d.units}
            supabase={supabase}
            setPage={d.setPage}
            selectedPeriod={selectedPeriod}
            landlordId={d.landlordId}
          />
        )}
        {d.page === "Documents" && (
          <Documents
            docsTab={d.docsTab} setDocsTab={d.setDocsTab}
            customTemplates={d.customTemplates} savedDocs={d.savedDocs}
            showDocModal={d.showDocModal} setShowDocModal={d.setShowDocModal}
            selectedTemplate={d.selectedTemplate}
            docTenant={d.docTenant} setDocTenant={d.setDocTenant}
            docExtras={d.docExtras} setDocExtras={d.setDocExtras}
            docBody={d.docBody} setDocBody={d.setDocBody}
            docSubject={d.docSubject} setDocSubject={d.setDocSubject}
            docSaved={d.docSaved}
            tenants={d.tenants} settings={d.settings}
            openDocModal={d.openDocModal} saveDocument={d.saveDocument} deleteDocument={d.deleteDocument}
            viewSavedDoc={d.viewSavedDoc} setViewSavedDoc={d.setViewSavedDoc}
            showNewTemplate={d.showNewTemplate} setShowNewTemplate={d.setShowNewTemplate}
            newTemplate={d.newTemplate} setNewTemplate={d.setNewTemplate}
            saveCustomTemplate={d.saveCustomTemplate}
            editTemplate={d.editTemplate} setEditTemplate={d.setEditTemplate}
            updateCustomTemplate={d.updateCustomTemplate}
            deleteCustomTemplate={d.deleteCustomTemplate} can={d.can}
            setShowLeaseWizard={d.setShowLeaseWizard}
          />
        )}
        {d.page === "Communications" && (
          <Communications
            communications={d.communications} tenants={d.tenants} can={d.can}
            commSearch={d.commSearch} setCommSearch={d.setCommSearch}
            commCatFilter={d.commCatFilter} setCommCatFilter={d.setCommCatFilter}
            commPriFilter={d.commPriFilter} setCommPriFilter={d.setCommPriFilter}
            commTenantFilter={d.commTenantFilter} setCommTenantFilter={d.setCommTenantFilter}
            commDateFrom={d.commDateFrom} setCommDateFrom={d.setCommDateFrom}
            commDateTo={d.commDateTo} setCommDateTo={d.setCommDateTo}
            showAddComm={d.showAddComm} setShowAddComm={d.setShowAddComm}
            newComm={d.newComm} setNewComm={d.setNewComm} addComm={d.addComm}
            showCommLog={d.showCommLog} setShowCommLog={d.setShowCommLog}
            deleteComm={d.deleteComm}
          />
        )}
        {(d.page === "Settings" || d.page === "Team") && (
          <Settings
            page={d.page} can={d.can}
            settings={d.settings} setSettings={d.setSettings}
            settingsSaved={d.settingsSaved} saveSettings={d.saveSettings}
            teamMembers={d.teamMembers} newMember={d.newMember} setNewMember={d.setNewMember}
            showAddMember={d.showAddMember} setShowAddMember={d.setShowAddMember}
            addTeamMember={d.addTeamMember} resendInvite={d.resendInvite}
            removeTeamMember={d.removeTeamMember} updateMemberRole={d.updateMemberRole}
            companyProfile={d.companyProfile} setCompanyProfile={d.setCompanyProfile}
            companyProfileSaved={d.companyProfileSaved} logoSaved={d.logoSaved} saveCompanyProfile={d.saveCompanyProfile}
            session={d.session}
            subTab={subTab}
            setPage={d.setPage}
          />
        )}
        {d.page === "HelpFees" && (
          <HelpFees setPage={d.setPage} />
        )}
      </div>

      {/* ══ MOBILE BOTTOM NAV ══ */}
      <div className="ls-mobile-nav">
        {mobileMain.map(n => (
          <button key={n} className={`ls-mob-item${d.page === n ? " active" : ""}`} onClick={() => { d.setPage(n); setShowMoreMenu(false); }}>
            <MobileIcon name={n} />
            <span>{n}</span>
          </button>
        ))}
        {mobileMore.length > 0 && (
          <button className={`ls-mob-item${showMoreMenu ? " active" : ""}`} onClick={() => setShowMoreMenu(s => !s)}>
            <MobileIcon name="More" />
            <span>More</span>
          </button>
        )}
      </div>

      {/* More drawer */}
      {showMoreMenu && (
        <div className={`ls-more-overlay${showMoreMenu ? " open" : ""}`} onClick={() => setShowMoreMenu(false)}>
          <div className="ls-more-drawer" onClick={e => e.stopPropagation()}>
            <div style={{ gridColumn: "1/-1", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
              <span style={{ fontSize: 10, color: "rgba(245,241,234,0.45)", fontFamily: "inherit", textTransform: "uppercase", letterSpacing: "0.08em" }}>More</span>
              <button onClick={() => setShowMoreMenu(false)} style={{ border: "none", background: "transparent", color: "rgba(245,241,234,0.45)", fontSize: 16, cursor: "pointer", lineHeight: 1 }}>✕</button>
            </div>
            {mobileMore.map(n => (
              <button key={n} className={`ls-more-item${d.page === n ? " active" : ""}`} onClick={() => { d.setPage(n); setShowMoreMenu(false); }}>
                <span>{n}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Lease Generator — global so it's reachable from the sidebar quick-add
          regardless of which page is currently active, not just from
          Documents/Properties → Leases. */}
      {d.showLeaseWizard && (
        <LeaseWizard
          tenants={d.tenants} properties={d.properties} settings={d.settings} companyProfile={d.companyProfile}
          onClose={() => d.setShowLeaseWizard(false)}
          submitLease={d.submitLease} leaseActionPending={d.leaseActionPending}
          leaseActionError={d.leaseActionError} setLeaseActionError={d.setLeaseActionError}
          setPage={d.setPage} setPropTab={d.setPropTab}
          checkLeaseConflict={d.checkLeaseConflict}
        />
      )}
    </div>
  );
}
